import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useCart } from '@/hooks/useCart';

export default function CartDrawer() {
  const { items, isOpen, closeCart, removeItem, updateQuantity, totalItems, totalPrice } = useCart();

  // Lock body scroll when drawer is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black/40 z-[60] transition-opacity duration-300 ${
          isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        onClick={closeCart}
      />

      {/* Drawer */}
      <aside
        className={`fixed top-0 right-0 h-full w-full max-w-[420px] bg-white z-[70] flex flex-col transition-transform duration-400 ease-in-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-stone-100">
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-semibold text-[#2c2420]" style={{ fontFamily: "'Playfair Display', serif" }}>
              Your Cart
            </h2>
            {totalItems > 0 && (
              <span className="bg-[#8B6348] text-white text-xs font-bold px-2 py-0.5 rounded-full">
                {totalItems}
              </span>
            )}
          </div>
          <button
            onClick={closeCart}
            className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-stone-100 transition-colors cursor-pointer text-stone-500"
            aria-label="Close cart"
          >
            <i className="ri-close-line text-xl" />
          </button>
        </div>

        {/* Cart Items */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {items.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center pb-16">
              <div className="w-16 h-16 flex items-center justify-center bg-[#f5f0ea] rounded-full mb-4">
                <i className="ri-shopping-bag-line text-2xl text-[#8B6348]" />
              </div>
              <p className="text-[#2c2420] font-medium mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                Your cart is empty
              </p>
              <p className="text-stone-400 text-sm mb-6">Discover our premium furniture collection</p>
              <Link
                to="/products"
                onClick={closeCart}
                className="whitespace-nowrap px-6 py-2.5 bg-[#2c2420] text-white text-sm font-medium rounded-full hover:bg-[#8B6348] transition-colors cursor-pointer"
              >
                Browse Products
              </Link>
            </div>
          ) : (
            <div className="space-y-4">
              {items.map((item) => (
                <div key={item.id} className="flex gap-4 bg-[#faf8f5] rounded-2xl p-4">
                  <div className="w-20 h-20 rounded-xl overflow-hidden flex-shrink-0">
                    <img
                      src={item.image}
                      alt={item.name}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] uppercase tracking-widest text-stone-400 mb-0.5">{item.category}</p>
                    <h4 className="text-[#2c2420] font-medium text-sm leading-snug mb-2 truncate" style={{ fontFamily: "'Playfair Display', serif" }}>
                      {item.name}
                    </h4>
                    <p className="text-[#8B6348] font-semibold text-sm mb-3">
                      ${(item.price * item.quantity).toLocaleString()}
                    </p>
                    <div className="flex items-center justify-between">
                      {/* Quantity Controls */}
                      <div className="flex items-center gap-1 bg-white rounded-full border border-stone-200 px-1 py-0.5">
                        <button
                          onClick={() => updateQuantity(item.id, item.quantity - 1)}
                          className="w-6 h-6 flex items-center justify-center text-stone-500 hover:text-[#8B6348] cursor-pointer transition-colors"
                        >
                          <i className="ri-subtract-line text-sm" />
                        </button>
                        <span className="text-[#2c2420] text-sm font-medium w-5 text-center">{item.quantity}</span>
                        <button
                          onClick={() => updateQuantity(item.id, item.quantity + 1)}
                          className="w-6 h-6 flex items-center justify-center text-stone-500 hover:text-[#8B6348] cursor-pointer transition-colors"
                        >
                          <i className="ri-add-line text-sm" />
                        </button>
                      </div>
                      <button
                        onClick={() => removeItem(item.id)}
                        className="w-7 h-7 flex items-center justify-center text-stone-300 hover:text-red-400 cursor-pointer transition-colors"
                      >
                        <i className="ri-delete-bin-line text-base" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {items.length > 0 && (
          <div className="border-t border-stone-100 px-6 py-5 space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-stone-500 text-sm">Subtotal ({totalItems} items)</span>
              <span className="text-[#2c2420] font-bold text-lg" style={{ fontFamily: "'Playfair Display', serif" }}>
                ${totalPrice.toLocaleString()}
              </span>
            </div>
            <p className="text-stone-400 text-xs">Shipping & taxes calculated at checkout</p>
            <Link
              to="/cart"
              onClick={closeCart}
              className="whitespace-nowrap w-full py-4 bg-[#2c2420] text-white text-sm font-semibold tracking-wider rounded-xl hover:bg-[#8B6348] transition-colors duration-200 cursor-pointer flex items-center justify-center gap-2"
            >
              <span>Proceed to Checkout</span>
              <i className="ri-arrow-right-line" />
            </Link>
            <button
              onClick={closeCart}
              className="whitespace-nowrap w-full py-3 text-stone-500 text-sm hover:text-[#2c2420] transition-colors cursor-pointer text-center"
            >
              Continue Shopping
            </button>
          </div>
        )}
      </aside>
    </>
  );
}
